import { AnyScale, ScaleValue, TicksSpec } from '@dfnivo/scales'
import { CompleteTheme } from '@dfnivo/core'
import { computeGridLines } from '../compute'
import { CanvasLine } from '@dfnivo/canvas'
import { useMemo } from 'react'

export type CanvasGridLineProps<Value extends ScaleValue> = {
    width: number
    height: number
    scale: AnyScale
    axis: 'x' | 'y'
    values?: TicksSpec<Value>
    theme: CompleteTheme
}

export const getCanvasGridLines = <Value extends ScaleValue>({
    width,
    height,
    scale,
    axis,
    values,
    theme,
}: CanvasGridLineProps<Value>) => {
    const lines = computeGridLines({ width, height, scale, axis, values })
    return lines.map(
        line =>
            new CanvasLine<void>(
                {
                    ...line,
                    lineWidth: parseFloat(String(theme.grid?.line?.strokeWidth)),
                    strokeStyle: theme.grid?.line?.stroke as string,
                    strokeDasharray: String(theme.grid.line.strokeDasharray || '') || undefined,
                },
                undefined
            )
    )
}

export const useCanvasGridLines = <Value extends ScaleValue>({
    enable,
    width,
    height,
    scale,
    axis,
    values,
    theme,
}: CanvasGridLineProps<Value> & { enable?: boolean }) =>
    useMemo(
        () =>
            enable
                ? getCanvasGridLines({
                      width,
                      height,
                      scale,
                      axis,
                      values,
                      theme,
                  })
                : [],
        [enable, width, height, scale, axis, values, theme]
    )

export const renderGridLinesToCanvas = <Value extends ScaleValue>(
    ctx: CanvasRenderingContext2D,
    props: CanvasGridLineProps<Value>
) => {
    getCanvasGridLines<Value>(props).forEach(line => {
        line.render(ctx)
    })
}
